import { type PostSessionWorkflowInput } from '@fruitful/types';
import { type WorkflowState } from '@mastra/core/workflows';
import { useStytchMemberSession } from '@stytch/react/b2b';
import { useQueryClient } from '@tanstack/react-query';
import { useAtom, useAtomValue } from 'jotai';
import React, { useCallback, useEffect } from 'react';
import {
    CircleAlertIcon,
    FullScreenSpinner,
    Icon,
    useToastController,
    View,
} from 'smoothie';

import { NoAccessYet } from '../../auth/components/NoAccess';
import { isLoggingOutRef } from '../../auth/helpers';
import FullScreenError from '../../common/components/FullScreenError';
import type {
    CommonMutationOptions,
    ContainerComponent,
} from '../../common/types/Common';
import {
    DEV_OVERRIDE_ADMIN_USER_GUIDE_ID,
    DEV_OVERRIDE_ADMIN_USER_ROLE,
} from '../../config/config';
import { IS_NON_PROD_ENV } from '../../config/FruitfulEnvironment';
import { GuideMessagingProvider } from '../../guide-messaging/providers/GuideMessagingProvider';
import { requestBuilder } from '../../http/RequestBuilder';
import { captureException, identifyUser } from '../../logger/Logger';
import {
    adminUserAtom,
    mutatedAdminUserGuideAtom,
    selectedMembershipAtom,
    selectedWorkflowRunAtom,
} from '../entityAtoms';
import {
    type AdminUser,
    AdminUserRole,
    type Authorization,
    type Guide,
    GuideStatus,
    type GuideWithAdvisees,
    type MembershipDetails,
} from '../entityTypes';

export const getAdminUserQueryKey = 'getAdminUser';

const useGetAdminUser = requestBuilder.query<AdminUser>({
    endpoint: '/api/admin/admin-user',
    queryKey: getAdminUserQueryKey,
});

export const AdminUserEntityProvider: ContainerComponent = ({ children }) => {
    const [adminUser, setAdminUser] = useAtom(adminUserAtom);
    const { session } = useStytchMemberSession();
    const selectedMembership = useAtomValue(selectedMembershipAtom);
    const selectedWorkflowRun = useAtomValue(selectedWorkflowRunAtom);

    const { data: fetchedAdminUser, isLoading: isLoadingUser } =
        useGetAdminUser(null, {
            select: useCallback(
                (user: AdminUser) => {
                    return {
                        ...handleOverrides(user),
                        authorization: getAuthorization(
                            user,
                            selectedMembership,
                            selectedWorkflowRun,
                        ),
                    };
                },
                [selectedMembership, selectedWorkflowRun],
            ),
            refetchOnWindowFocus: false,
            refetchOnMount: false,
            refetchOnReconnect: false,
            refetchInterval: 1800000, // 30 min
        });

    useEffect(() => {
        if (
            isLoggingOutRef.current ||
            session == null ||
            fetchedAdminUser == null
        ) {
            return;
        }

        setAdminUser(fetchedAdminUser);
        identifyUser(fetchedAdminUser);
    }, [fetchedAdminUser, session]);

    useAdminUserMutations();

    const toast = useToastController();

    useEffect(() => {
        if (
            [AdminUserRole.GUIDE, AdminUserRole.GUIDE_LEAD].includes(
                adminUser.role,
            ) &&
            (adminUser.guideId == null || adminUser.guide == null)
        ) {
            toast.show(
                `User has role '${adminUser.role}', but no associated Guide.`,
                {
                    duration: 100000,
                    description: 'Reach out to engineering for support.',
                    icon: (
                        <View
                            backgroundColor="$red900"
                            padding={8}
                            borderRadius={16}
                        >
                            <Icon icon={CircleAlertIcon} color="$white" />
                        </View>
                    ),
                },
            );
        }
    }, [adminUser.role, adminUser.guideId, adminUser.guide]);

    if (adminUser.id === null && !fetchedAdminUser && !isLoadingUser) {
        captureException(new Error('Active session, but no admin user found.'));
        return <FullScreenError message="No admin user found." />;
    }

    if (adminUser.id === null) {
        return <FullScreenSpinner />;
    }

    if (!adminUser.authorization.app?.canUse) {
        return <NoAccessYet />;
    }

    if (adminUser.authorization.guideMessaging?.canUse) {
        return <GuideMessagingProvider>{children}</GuideMessagingProvider>;
    }

    return <>{children}</>;
};

const handleOverrides = (user: AdminUser): AdminUser => {
    if (DEV_OVERRIDE_ADMIN_USER_GUIDE_ID != null) {
        const overrideGuideId = DEV_OVERRIDE_ADMIN_USER_GUIDE_ID ?? '';
        const overrideGuide: GuideWithAdvisees = {
            id: user.guideId ?? '',
            firstName: 'Override',
            lastName: 'Guide',
            email: '',
            status: GuideStatus.ACTIVE,
            advisees: [],
            createdAt: new Date(),
            updatedAt: null,
        };
        user.guideId = overrideGuideId;
        user.guide =
            user.guide == null
                ? overrideGuide
                : { ...user.guide, id: overrideGuideId };
    }

    user.role = (DEV_OVERRIDE_ADMIN_USER_ROLE as AdminUserRole) ?? user.role;

    return user;
};

const getAuthorization = (
    user: AdminUser,
    selectedMembership: MembershipDetails | null,
    selectedWorkflowRun: WorkflowState | null,
): Authorization => {
    const Role = AdminUserRole;

    const hasGuideAttributes = user.guideId != null && user.guide != null;
    const hasGuideMessagingAttributes = user.guide?.sendbirdUserId != null;

    const isGuide = user.role === Role.GUIDE && hasGuideAttributes;
    const isGuideLead = user.role === Role.GUIDE_LEAD && hasGuideAttributes;
    const isGuideWithMessaging =
        (isGuide || isGuideLead) && hasGuideMessagingAttributes;

    const isSuperAdmin = user.role === Role.SUPERADMIN;
    const isAdventureEngineer = [
        'kim@fruitful.co',
        'alaughlin@fruitful.co',
        'jacob@fruitful.co',
        'cass@fruitful.co',
    ].includes(user.email);
    const hasAccessToBillsDevTab =
        isAdventureEngineer || user.email === 'jenni@fruitful.co';
    const isMemberOps = user.role === Role.MEMBER_OPS;
    const isEngineer = user.role === Role.ENGINEER;
    const isGeneral = user.role === Role.GENERAL;

    const isGuideAdvisorForMembership =
        (isGuide || isGuideLead) &&
        user.guide?.advisees.find(
            (advisee) => advisee.id === selectedMembership?.guideId,
        ) != null;

    const isGuideForMembership =
        (isGuide || isGuideLead) &&
        user.guideId === selectedMembership?.guideId;

    const isComplianceOps = [
        'chelsie@fruitful.co',
        'john@fruitful.co',
        'jill@fruitful.co',
    ].includes(user.email);

    const isBankOps =
        isSuperAdmin ||
        [
            'bob@fruitful.co',
            'john@fruitful.co',
            'neczar@fruitful.co',
            'katie@fruitful.co',
            'jill@fruitful.co',
            'jason@fruitful.co',
            'jacob@fruitful.co',
            'chelsie@fruitful.co',
            'ifi@fruitful.co',
            'malissa@fruitful.co',
        ].includes(user.email);

    const isImOps =
        isSuperAdmin ||
        [
            'bob@fruitful.co',
            'john@fruitful.co',
            'neczar@fruitful.co',
            'katie@fruitful.co',
            'jill@fruitful.co',
            'jason@fruitful.co',
            'jacob@fruitful.co',
            'chelsie@fruitful.co',
            'caleb@fruitful.co',
            'colin@fruitful.co',
        ].includes(user.email);

    const isWorkflowsQACohort = [
        'srubin@fruitful.co',
        'cass@fruitful.co',
        'caleb@fruitful.co',
        'rachel@fruitful.co',
        'anya@fruitful.co',
    ].includes(user.email);

    const isFruitfulEmployeeMembership = selectedMembership?.users.some(
        ({ adminUser }) => !!adminUser,
    );

    const isSelf =
        selectedMembership?.users
            .map(({ id }) => id)
            .includes(user.memberId ?? '') ?? false;

    // Workflow run authorization checks
    const isWorkflowRunForGuide =
        selectedWorkflowRun === null
            ? false
            : (
                  selectedWorkflowRun.payload as
                      | PostSessionWorkflowInput
                      | undefined
              )?.guideEmailAddress.toLowerCase() === user.email.toLowerCase();

    return {
        selectedMembership: {
            general: {
                canView:
                    isSelf ||
                    isGuideForMembership ||
                    isGuideAdvisorForMembership ||
                    isMemberOps ||
                    ((isGuideLead || isSuperAdmin || isEngineer) &&
                        !isFruitfulEmployeeMembership),
                canEdit:
                    isSelf ||
                    isGuideForMembership ||
                    isMemberOps ||
                    ((isGuideLead || isSuperAdmin) &&
                        !isFruitfulEmployeeMembership),
            },
            pii: {
                canView:
                    isSelf ||
                    isGuideForMembership ||
                    isGuideAdvisorForMembership ||
                    isGuideLead ||
                    isMemberOps ||
                    isSuperAdmin ||
                    isEngineer,
                canEdit: isMemberOps || isSuperAdmin,
            },
            accounts: {
                canView:
                    isSelf ||
                    isGuideForMembership ||
                    isGuideAdvisorForMembership ||
                    isGuideLead ||
                    isMemberOps ||
                    isSuperAdmin,
                canEdit:
                    isGuideForMembership ||
                    isGuideLead ||
                    isMemberOps ||
                    isSuperAdmin,
            },
            addresses: {
                canView:
                    isSelf ||
                    isGuideForMembership ||
                    isGuideAdvisorForMembership ||
                    isGuideLead ||
                    isMemberOps ||
                    isSuperAdmin,
                canEdit: isMemberOps || isSuperAdmin,
            },
            bills: {
                canView:
                    isSelf ||
                    isGuideForMembership ||
                    isGuideAdvisorForMembership ||
                    isGuideLead ||
                    isMemberOps ||
                    isSuperAdmin,
                canEdit: isGuideForMembership || isSuperAdmin,
            },
            goals: {
                canView:
                    isSelf ||
                    isGuideForMembership ||
                    isGuideAdvisorForMembership ||
                    isGuideLead ||
                    isSuperAdmin,
                canEdit: isGuideForMembership || isSuperAdmin,
            },
            bankOps: {
                canUse: isSuperAdmin || isMemberOps,
            },
            guidanceSessions: {
                canView:
                    isSelf ||
                    isGuideForMembership ||
                    isGuideAdvisorForMembership ||
                    isGuideLead ||
                    isMemberOps ||
                    isSuperAdmin ||
                    (isEngineer && !isFruitfulEmployeeMembership),
                canEdit:
                    isGuideForMembership ||
                    isGuideAdvisorForMembership ||
                    isGuideLead ||
                    isMemberOps ||
                    isSuperAdmin,
            },
            cancelMembership: {
                canUse: isMemberOps || isSuperAdmin,
            },
            changeSubscription: {
                canUse: isMemberOps || isSuperAdmin,
            },
            removeFromMembership: {
                canUse: isMemberOps || isSuperAdmin,
            },
            addPartner: {
                canUse: isGuide || isGuideLead || isMemberOps || isSuperAdmin,
            },
            notes: {
                canView:
                    isSelf ||
                    isGuideForMembership ||
                    isGuideAdvisorForMembership ||
                    isGuideLead ||
                    isSuperAdmin,
                canEdit:
                    isSelf ||
                    isGuideForMembership ||
                    isGuideAdvisorForMembership ||
                    isGuideLead,
            },
            investmentProfile: {
                canView:
                    isSelf ||
                    isGuideForMembership ||
                    isGuideAdvisorForMembership ||
                    isGuideLead ||
                    isMemberOps ||
                    isSuperAdmin ||
                    IS_NON_PROD_ENV,
                canEdit:
                    isGuideForMembership ||
                    isGuideAdvisorForMembership ||
                    isGuideLead ||
                    IS_NON_PROD_ENV,
            },
        },
        memberships: {
            canViewAll:
                isGeneral ||
                isSuperAdmin ||
                isGuideLead ||
                isGuide ||
                isMemberOps ||
                isEngineer,
        },
        guideMessaging: {
            canUse: isGuideWithMessaging,
            canModerate:
                isGuideLead ||
                isGuideAdvisorForMembership ||
                isSuperAdmin ||
                isMemberOps,
        },
        workflows: {
            canUse:
                isSuperAdmin || isWorkflowsQACohort || isGuideLead || isGuide,
        },
        selectedWorkflowRun: {
            canView:
                isSuperAdmin || isWorkflowsQACohort || isWorkflowRunForGuide,
            canEdit: isWorkflowRunForGuide,
        },
        guides: {
            canViewAll:
                isSuperAdmin ||
                isGuideLead ||
                isGuide ||
                isMemberOps ||
                isEngineer ||
                IS_NON_PROD_ENV,
            canEditAll:
                isSuperAdmin ||
                isGuideLead ||
                isMemberOps ||
                isEngineer ||
                IS_NON_PROD_ENV,
        },
        bankOpsFunctionality: {
            canUse: isBankOps,
        },
        complianceOpsFunctionality: {
            canUse: isComplianceOps || isSuperAdmin,
        },
        imOpsFunctionality: {
            canUse: isImOps,
        },
        adminUsers: {
            canViewAll: isSuperAdmin || isEngineer || IS_NON_PROD_ENV,
            canEditAll: isSuperAdmin || IS_NON_PROD_ENV,
        },
        app: {
            canUse: true,
        },
        aiPrompts: {
            canUse: isSuperAdmin || isGuideLead || isEngineer,
        },
        betaTransactionFeatures: {
            canUse: hasAccessToBillsDevTab,
        },
    };
};

const useAdminUserMutations = () => {
    const queryClient = useQueryClient();
    const commonMutationOptions: CommonMutationOptions = {
        onMutate: () => {
            void queryClient.cancelQueries({
                queryKey: [getAdminUserQueryKey],
                exact: true,
            });
        },
        onSuccess: () => {},
        onSettled: () => {
            void queryClient.invalidateQueries({
                queryKey: [getAdminUserQueryKey],
                exact: true,
            });
        },
    };

    useHandleAdminUserGuideUpdate(commonMutationOptions);
};

const useMutateAdminUserGuide = requestBuilder.mutation<
    Guide,
    Pick<Guide, 'oooStart' | 'oooEnd'>,
    Pick<Guide, 'id'>
>({
    endpoint: ({ id }) => `/api/admin/guides/${id}`,
    method: 'PATCH',
});

const useHandleAdminUserGuideUpdate = (
    mutationOptions: CommonMutationOptions,
) => {
    const [mutatedAdminUserGuide, setMutatedAdminUserGuide] = useAtom(
        mutatedAdminUserGuideAtom,
    );
    const { mutate: mutateAdminUserGuide } =
        useMutateAdminUserGuide(mutationOptions);
    useEffect(() => {
        if (mutatedAdminUserGuide !== null) {
            const { oooStart, oooEnd } = mutatedAdminUserGuide;

            mutateAdminUserGuide({
                urlParams: { id: mutatedAdminUserGuide.id },
                body: {
                    oooStart,
                    oooEnd,
                },
            });
            setMutatedAdminUserGuide(null);
        }
    }, [mutatedAdminUserGuide]);
};
